/**
 * Program 12.1 UX3 — Deterministic lead readiness (operational guidance, not approval authority).
 */
export type LeadReadinessItem = {
  id: string;
  label: string;
  state: 'established' | 'partial' | 'missing';
  detail?: string;
};

export type LeadReadinessResult = {
  items: LeadReadinessItem[];
  establishedCount: number;
  total: number;
  signalQuality: 'LOW' | 'MEDIUM' | 'HIGH';
};

function asStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function buildLeadReadiness(lead: Record<string, unknown>): LeadReadinessResult {
  const customer = asStr(lead.customer_name);
  const project =
    asStr(lead.description) || asStr(lead.business_challenge) || asStr(lead.required_standards);
  const scope = asStr(lead.business_challenge) || asStr(lead.description);
  const value = num(lead.estimated_project_value) || num(lead.annual_revenue);
  const timing =
    lead.next_follow_up_date ||
    lead.expected_timeline_weeks != null ||
    asStr(lead.last_contact_date);
  const contact = asStr(lead.contact_person) || asStr(lead.email) || asStr(lead.phone);
  const nextAction = lead.next_follow_up_date;

  const items: LeadReadinessItem[] = [
    {
      id: 'customer',
      label: 'Customer',
      state: customer ? 'established' : 'missing',
    },
    {
      id: 'project',
      label: 'Project',
      state: project ? 'established' : 'missing',
    },
    {
      id: 'scope',
      label: 'Scope',
      state: scope ? 'established' : 'partial',
    },
    {
      id: 'value',
      label: 'Value',
      state: value > 0 ? 'established' : 'partial',
      detail: value > 0 ? undefined : 'Value not established',
    },
    {
      id: 'timing',
      label: 'Timing',
      state: timing ? 'established' : 'partial',
    },
    {
      id: 'contact',
      label: 'Primary Contact',
      state: contact ? 'established' : 'missing',
    },
    {
      id: 'next_action',
      label: 'Next Action',
      state: nextAction ? 'established' : 'missing',
    },
  ];

  const establishedCount = items.filter((i) => i.state === 'established').length;
  const total = items.length;
  let signalQuality: LeadReadinessResult['signalQuality'] = 'LOW';
  if (establishedCount >= 6) signalQuality = 'HIGH';
  else if (establishedCount >= 4) signalQuality = 'MEDIUM';

  return { items, establishedCount, total, signalQuality };
}

export type LeadPrimaryCta = {
  key: 'convert' | 'qualify' | 'disqualify' | 'open_opportunity' | 'none';
  label: string;
  disabled?: boolean;
  reason?: string;
};

export function resolveLeadPrimaryCta(lead: Record<string, unknown>): LeadPrimaryCta {
  const status = asStr(lead.lead_status).toLowerCase();
  if (lead.is_converted && lead.converted_to_opportunity_id) {
    return { key: 'open_opportunity', label: 'Open Opportunity' };
  }
  if (status === 'unqualified' || status === 'lost') {
    return { key: 'none', label: 'Closed', disabled: true };
  }
  if (status === 'new' || status === 'contacted') {
    return { key: 'qualify', label: 'Qualify Lead' };
  }
  if (status === 'qualified') {
    return { key: 'convert', label: 'Convert to Opportunity' };
  }
  return { key: 'qualify', label: 'Qualify Lead' };
}

/** Fields carried forward on lead → opportunity conversion (preview). */
export function leadConversionCarryForward(lead: Record<string, unknown>): string[] {
  const rows: string[] = [];
  if (asStr(lead.customer_name)) rows.push('Customer name');
  if (asStr(lead.contact_person)) rows.push('Contact person');
  if (asStr(lead.email)) rows.push('Email');
  if (asStr(lead.phone)) rows.push('Phone');
  if (asStr(lead.industry)) rows.push('Industry');
  if (num(lead.estimated_project_value) || num(lead.annual_revenue)) rows.push('Estimated value');
  if (lead.revenue_service_type_id) rows.push('Revenue service type');
  if (asStr(lead.business_challenge)) rows.push('Business challenge / scope signal');
  if (lead.sales_team_id) rows.push('Sales team');
  return rows;
}

export function leadConversionRequires(): string[] {
  return ['Revenue service type', 'Sales owner', 'Enterprise decision checkpoint (all pass)'];
}
